import React, { useState } from 'react';
import { Eye, Heart, MessageCircle, BookOpen } from 'lucide-react';
import AttestBadge from '../AttestBadge';
import { categories } from '../../constants';
import { useTheme } from '../../ThemeContext';


export default function HomeView({ 
  posts, 
  loading,
  searchQuery,
  viewPost
}) {
  const { t, isDark } = useTheme();
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [hoveredCat, setHoveredCat] = useState(null);

  const activeCategories = categories.filter(cat => posts.some(p => p.category === cat));
  const allCategories = ['All', ...activeCategories];

  const filteredPosts = posts.filter(post => {
    const matchesSearch = !searchQuery || 
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.subtitle?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.category.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const featuredPost = posts[0];

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <style>{`
        .cat-scroll { overflow-x: auto; scrollbar-width: none; -ms-overflow-style: none; }
        .cat-scroll::-webkit-scrollbar { display: none; }
      `}</style>

      {loading ? (
        <div className="text-center py-20">
          <div className="inline-block w-8 h-8 border-4 border-stone-300 rounded-full animate-spin" style={{ borderTopColor: t.accent }} />
        </div>
      ) : (
        <>
          {/* Featured Story */}
          {featuredPost && !searchQuery && (
            <div className="mb-16 animate-in">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <span className="mono text-xs uppercase tracking-wider" style={{ color: t.text }}>
                    Featured Story
                  </span>
                  <h2 
                    className="serif-display text-5xl md:text-6xl my-4 leading-tight cursor-pointer transition-opacity"
                    onClick={() => viewPost(featuredPost)}
                    onMouseEnter={(e) => e.target.style.opacity = '0.7'}
                    onMouseLeave={(e) => e.target.style.opacity = '1'}
                  >
                    {featuredPost.title}
                  </h2>
                  {featuredPost.subtitle && (
                    <p className="text-xl text-stone-600 mb-6">{featuredPost.subtitle}</p>
                  )}
                  <div className="flex items-center gap-4 text-sm text-stone-500">
                    <span className="mono">{featuredPost.author}</span>
                    <span>•</span>
                    <span>{featuredPost.read_time} min read</span>
                    <span>•</span>
                    <span>{new Date(featuredPost.timestamp).toLocaleDateString()}</span>
                  </div>
                  <div className="flex items-center gap-6 mt-6 text-stone-500">
                    <span className="flex items-center gap-2">
                      <Eye size={16} />
                      {featuredPost.views || 0}
                    </span>
                    <span className="flex items-center gap-2">
                      <Heart size={16} />
                      {featuredPost.like_count || 0}
                    </span>
                    <span className="flex items-center gap-2">
                      <MessageCircle size={16} />
                      {featuredPost.comment_count || 0}
                    </span>
                    {featuredPost.attest_tx_hash && (
                      <AttestBadge txHash={featuredPost.attest_tx_hash} />
                    )}
                  </div>
                </div>
                {/* Featured Post Image */}
                <div 
                  className="rounded-2xl h-80 flex items-center justify-center overflow-hidden cursor-pointer"
                  style={{
                    backgroundColor: featuredPost.featured_image_url ? 'transparent' : '#e7e5e4'
                  }}
                  onClick={() => viewPost(featuredPost)}
                >
                  {featuredPost.featured_image_url ? (
                    <img
                      src={featuredPost.featured_image_url}
                      alt={featuredPost.title}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        borderRadius: '16px'
                      }}
                    />
                  ) : (
                    <BookOpen size={64} style={{ color: t.text, opacity: 0.2 }} />
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Categories */}
          {!searchQuery && (
            <div style={{ marginBottom: '48px' }}>
              <div className="cat-scroll" style={{
                display: 'flex',
                gap: '8px',
                padding: '4px 0',
              }}>
                {allCategories.map(cat => {
                  const isActive = selectedCategory === cat;
                  const isHovered = hoveredCat === cat && !isActive;
                  return (
                    <button
                      key={cat}
                      onClick={() => setSelectedCategory(cat)}
                      onMouseEnter={() => setHoveredCat(cat)}
                      onMouseLeave={() => setHoveredCat(null)}
                      style={{
                        flexShrink: 0,
                        padding: '6px 18px',
                        borderRadius: '999px',
                        border: `1px solid ${isActive ? 'transparent' : (t.borderStrong || t.border)}`,
                        backgroundColor: isActive
                          ? t.buttonBg
                          : isHovered
                            ? (isDark ? 'rgba(245,230,200,0.1)' : 'rgba(61,36,18,0.06)')
                            : (isDark ? 'rgba(245,230,200,0.06)' : '#ffffff'),
                        color: isActive ? t.buttonText : t.text,
                        fontFamily: "'Poppins', sans-serif",
                        fontSize: '13px',
                        fontWeight: isActive ? 600 : 400,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {cat}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Posts Grid */}
          {filteredPosts.length > 0 ? (
            <div className="grid md:grid-cols-3 gap-8">
              {filteredPosts.map((post, idx) => (
                <article 
                  key={post.id} 
                  className="hover-lift cursor-pointer animate-in"
                  style={{ animationDelay: `${idx * 0.1}s` }}
                  onClick={() => viewPost(post)}
                >
                  {/* Post Thumbnail */}
                  <div 
                    className="rounded-xl h-48 mb-4 flex items-center justify-center overflow-hidden"
                    style={{
                      backgroundColor: post.featured_image_url ? 'transparent' : '#e7e5e4'
                    }}
                  >
                    {post.featured_image_url ? (
                      <img
                        src={post.featured_image_url}
                        alt={post.title}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    ) : (
                      <BookOpen size={32} style={{ color: t.text, opacity: 0.3 }} />
                    )}
                  </div>
                  <span className="mono text-xs uppercase tracking-wider" style={{ color: t.text }}>
                    {post.category}
                  </span>
                  <h3 className="serif-display text-2xl my-2 leading-tight transition-opacity" onMouseEnter={(e) => e.target.style.opacity = '0.7'} onMouseLeave={(e) => e.target.style.opacity = '1'}>
                    {post.title}
                  </h3>
                  {post.subtitle && (
                    <p className="text-stone-600 mb-3 line-clamp-2">{post.subtitle}</p>
                  )}
                  <div className="flex items-center justify-between text-sm text-stone-500">
                    <div className="flex items-center gap-3">
                      <span className="flex items-center gap-1">
                        <Eye size={14} />
                        {post.views || 0}
                      </span>
                      <span className="flex items-center gap-1">
                        <Heart size={14} />
                        {post.like_count || 0}
                      </span>
                      <span className="flex items-center gap-1">
                        <MessageCircle size={14} />
                        {post.comment_count || 0}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      {post.attest_tx_hash && (
                        <AttestBadge txHash={post.attest_tx_hash} />
                      )}
                      <span>{post.read_time} min</span>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-stone-600">No posts found</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}